import React, { useEffect, Children } from 'react';
import { Link } from 'react-router-dom';
import { LeafIcon, RecycleIcon, BatteryChargingIcon } from 'lucide-react';
import { SectionHeader } from '../components/SectionHeader';
const values = [{
  name: 'Respect',
  colour: '#335F91',
  textColour: '#335F91',
  qualities: ['Listening and supporting others', 'Showing consideration', 'Following the rules']
}, {
  name: 'Friendship',
  colour: '#FFBC0A',
  textColour: '#92700C',
  qualities: ['Comradeship', 'Team work', 'Being brilliant']
}, {
  name: 'Determination',
  colour: '#1a1a1a',
  textColour: '#1a1a1a',
  qualities: ['Pushing to achieve your best', 'Strength of will', 'Working hard']
}, {
  name: 'Excellence',
  colour: '#2E7D32',
  textColour: '#2E7D32',
  qualities: ['Doing your best', 'Striving for improvement', 'Achieving your goals']
}, {
  name: 'Courage',
  colour: '#C33149',
  textColour: '#C33149',
  qualities: ['Excellent behaviour', 'Firm resolve', 'Focused attitude']
}, {
  name: 'Equality',
  colour: '#335F91',
  textColour: '#335F91',
  secondaryColours: ['#335F91', '#ffffff', '#C33149'],
  qualities: ['Equal treatment of all', 'Tolerance and understanding', 'Fair play']
}, {
  name: 'Inspiration',
  colour: '#d4d4d4',
  textColour: '#374151',
  qualities: ['Being a positive role model', 'Enjoying sport when you win or lose', 'Using your achievements to lead the way for others']
}];
const sustainabilityPoints = [{
  icon: <LeafIcon className="w-6 h-6" data-id="element-1083" />,
  title: 'Reduced Carbon Footprint',
  description: 'Lower environmental impact through responsible sourcing and local production.'
}, {
  icon: <RecycleIcon className="w-6 h-6" data-id="element-1084" />,
  title: 'Sustainable Materials',
  description: 'Crafted from responsibly sourced wood, a renewable and natural resource.'
}, {
  icon: <BatteryChargingIcon className="w-6 h-6" data-id="element-1085" />,
  title: 'Less Energy Used',
  description: 'Wooden medal production requires significantly less energy than metal alternatives.'
}];
export function MedalsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="bg-white pt-20" data-id="element-1086">
      {/* Hero */}
      <section className="relative overflow-hidden bg-brand-blue pt-28 pb-20 md:pt-36 md:pb-28 min-h-[320px] md:min-h-[400px]" data-id="element-1087">
        <img src="https://cdn.magicpatterns.com/uploads/pTRbE2fvqTGzPDd7dvdT5h/Group1.webp" alt="" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" data-id="element-1088" />
        <div className="absolute inset-0 bg-brand-blue/85" data-id="element-1089"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1090">
          <nav className="flex text-sm font-medium text-white/70 mb-8" data-id="element-1091">
            <Link to="/" className="hover:text-white transition-colors" data-id="element-1092">
              Home
            </Link>
            <span className="mx-2" data-id="element-1093">/</span>
            <span className="text-white" data-id="element-1094">Medals</span>
          </nav>

          <div className="max-w-3xl" data-id="element-1095">
            <p className="text-sm md:text-base tracking-[0.3em] uppercase text-white/70 mb-4" data-id="element-1096">
              Team Up With Olympians
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-6 leading-tight" data-id="element-1097">
              Medals
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed" data-id="element-1098">
              Celebrating the Olympic and Paralympic values through our award
              programme for young athletes.
            </p>
          </div>
        </div>
      </section>

      {/* Olympic Values Medals intro */}
      <section className="py-24" data-id="element-1099">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1100">
          <SectionHeader eyebrow="The Olympic Way" title="Olympic Values Medals" description="The Olympic Values can play an important part in life on and off the courts. We want to inspire young children to enjoy sport, learn new skills, build new friendships, strive to achieve their goals and grow in confidence. Children can earn these medals by showing these qualities:" data-id="element-1101" />

          {/* Value cards grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-1102">
            {values.map(value => {
            const isEquality = value.name === 'Equality';
            return <div key={value.name} className="bg-white rounded-2xl border border-navy-100 overflow-hidden shadow-sm flex flex-col" data-id="element-1103">
                  {/* Colour accent bar */}
                  {isEquality && value.secondaryColours ? <div className="flex h-2" data-id="element-1104">
                      {value.secondaryColours.map((c, i) => <div key={i} className="flex-1" style={{
                  backgroundColor: c
                }} data-id="element-1105" />)}
                    </div> : <div className="h-2" style={{
                backgroundColor: value.colour
              }} data-id="element-1106" />}

                  <div className="p-6 flex-1 flex flex-col" data-id="element-1107">
                    <h3 className="text-lg font-display font-bold uppercase tracking-wide mb-4" style={{
                  color: value.textColour
                }} data-id="element-1108">
                      {value.name}
                    </h3>

                    <ul className="space-y-2.5 flex-1" data-id="element-1109">
                      {value.qualities.map((quality, i) => <li key={i} className="flex items-start text-navy-600 text-sm leading-relaxed" data-id="element-1110">
                          <span className="w-1.5 h-1.5 rounded-full mt-1.5 mr-2.5 flex-shrink-0" style={{
                      backgroundColor: value.colour
                    }} data-id="element-1111" />
                          {quality}
                        </li>)}
                    </ul>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* Wooden medals / sustainability */}
      <section className="py-24 bg-emerald-900" data-id="element-1112">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1113">
          <div className="text-center mb-16" data-id="element-1114">
            <span className="block text-sm font-bold tracking-widest uppercase mb-3 text-emerald-300" data-id="element-1115">
              Sustainability
            </span>
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-display font-bold text-white mb-4" data-id="element-1116">
              Sustainable Wooden Medals
            </h3>
            <p className="text-lg text-emerald-200/70 leading-relaxed max-w-2xl mx-auto" data-id="element-1117">
              Our medals are made from sustainably sourced wood, reflecting our
              commitment to the environment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1118">
            {sustainabilityPoints.map(point => <div key={point.title} className="bg-emerald-800/50 border border-emerald-700/30 rounded-2xl p-6 text-center" data-id="element-1119">
                <div className="w-12 h-12 rounded-full bg-emerald-700/50 flex items-center justify-center mx-auto mb-4 text-emerald-300" data-id="element-1120">
                  {point.icon}
                </div>
                <h4 className="text-white font-display font-bold text-base mb-2" data-id="element-1121">
                  {point.title}
                </h4>
                <p className="text-emerald-200/60 text-sm leading-relaxed" data-id="element-1122">
                  {point.description}
                </p>
              </div>)}
          </div>
        </div>
      </section>
    </main>;
}